import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  Grid,
  Chip,
  Card,
  CardContent,
} from '@mui/material'
import Close from '@mui/icons-material/Close'
import Add from '@mui/icons-material/Add'
import Delete from '@mui/icons-material/Delete'
import PlayArrow from '@mui/icons-material/PlayArrow'
import List from '@mui/icons-material/List'
import Edit from '@mui/icons-material/Edit'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import ReactPlayer from 'react-player/youtube'
import { CustomInput, CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import {
  getPlayableExternalContentById,
  savePlayableExternalContent,
} from 'src/supportPortal/services/docSideService'
import { CONTENT_TYPES, EXCL_INCL_TYPES } from 'src/supportPortal/constants/docSideConstants'

export default function ContentDialog({
  open,
  mode,
  content,
  context,
  product,
  role,
  onClose,
  onSave,
}) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    type: CONTENT_TYPES.SINGLE_VIDEO,
    contentId: '',
    contentNumber: '',
    roles: [role.id],
    productIds: [product.id],
    contextIds: [context.id],
    exclInclUserIds: [],
    exclInclType: EXCL_INCL_TYPES.INCLUDE,
    availableTill: null,
    premierTime: null,
    publishedTime: null,
    playlistVideoIds: [],
  })

  const [newUserId, setNewUserId] = useState('')
  const [editingPlaylistItem, setEditingPlaylistItem] = useState(null)
  const [playlistItemForm, setPlaylistItemForm] = useState({
    name: '',
    contentId: '',
  })

  const isViewMode = mode === 'view'
  const isEditMode = mode === 'edit'
  const isAddMode = mode === 'add'

  // Load content details when editing/viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && content?.id) {
      loadContentDetails(content.id)
    } else if (isAddMode) {
      // Reset form for new content
      setFormData({
        name: '',
        type: CONTENT_TYPES.SINGLE_VIDEO,
        contentId: '',
        contentNumber: '',
        roles: [role.id],
        productIds: [product.id],
        contextIds: [context.id],
        exclInclUserIds: [],
        exclInclType: EXCL_INCL_TYPES.INCLUDE,
        availableTill: null,
        premierTime: null,
        publishedTime: null,
        playlistVideoIds: [],
      })
    }
  }, [mode, content, role.id, product.id, context.id])

  const loadContentDetails = async (contentId) => {
    setLoading(true)
    setError(null)
    try {
      const response = await getPlayableExternalContentById(contentId)
      if (response?.status === 200 && response.data) {
        const data = response.data
        setFormData({
          ...data,
          availableTill: data.availableTill ? dayjs(data.availableTill) : null,
          premierTime: data.premierTime ? dayjs(data.premierTime) : null,
          publishedTime: data.publishedTime ? dayjs(data.publishedTime) : null,
          playlistVideoIds: data.playlistVideoIds || [],
          exclInclUserIds: data.exclInclUserIds || [],
        })
      } else {
        setError('Failed to load content details')
      }
    } catch (err) {
      setError('Failed to load content details: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleAddPlaylistItem = () => {
    if (playlistItemForm.name.trim() && playlistItemForm.contentId.trim()) {
      const newItem = {
        apiParam: 'playable_external_content',
        enabled: true,
        deleted: false,
        idByUser: false,
        name: playlistItemForm.name.trim(),
        type: 'single_video',
        contentId: playlistItemForm.contentId.trim(),
      }

      if (editingPlaylistItem !== null) {
        // Update existing item
        setFormData((prev) => ({
          ...prev,
          playlistVideoIds: prev.playlistVideoIds.map((item, index) =>
            index === editingPlaylistItem ? newItem : item,
          ),
        }))
        setEditingPlaylistItem(null)
      } else {
        // Add new item
        setFormData((prev) => ({
          ...prev,
          playlistVideoIds: [...(prev.playlistVideoIds || []), newItem],
        }))
      }
      setPlaylistItemForm({ name: '', contentId: '' })
    }
  }

  const handleEditPlaylistItem = (index) => {
    const item = formData.playlistVideoIds[index]
    setPlaylistItemForm({ name: item.name, contentId: item.contentId })
    setEditingPlaylistItem(index)
  }

  const handleRemoveVideoId = (index) => {
    setFormData((prev) => ({
      ...prev,
      playlistVideoIds: prev.playlistVideoIds.filter((_, i) => i !== index),
    }))
  }

  const handleAddUserId = () => {
    if (newUserId.trim()) {
      setFormData((prev) => ({
        ...prev,
        exclInclUserIds: [...(prev.exclInclUserIds || []), newUserId.trim()],
      }))
      setNewUserId('')
    }
  }

  const handleRemoveUserId = (index) => {
    setFormData((prev) => ({
      ...prev,
      exclInclUserIds: prev.exclInclUserIds.filter((_, i) => i !== index),
    }))
  }

  const validateForm = () => {
    if (!formData.name?.trim()) {
      setError('Name is required')
      return false
    }
    if (!formData.contentId?.trim()) {
      setError('Content ID is required')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setSaving(true)
    setError(null)
    try {
      let dataToSave = {
        ...formData,
        apiParam: 'playable_external_content',
        availableTill: dayjs(formData.availableTill).utc().format('YYYY-MM-DD HH:mm:ss') || null,
        premierTime: dayjs(formData.premierTime).utc().format('YYYY-MM-DD HH:mm:ss') || null,
        publishedTime: dayjs(formData.publishedTime).utc().format('YYYY-MM-DD HH:mm:ss') || null,
      }
      if (!dataToSave?.exclInclUserIds?.length) {
        delete dataToSave.exclInclUserIds
        delete dataToSave.exclInclType
      }

      const response = await savePlayableExternalContent(dataToSave)
      if (response?.status === 200) {
        onSave()
      } else {
        setError('Failed to save content')
      }
    } catch (err) {
      setError('Failed to save content: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const getDialogTitle = () => {
    switch (mode) {
      case 'add':
        return 'Add New Content'
      case 'edit':
        return 'Edit Content'
      case 'view':
        return 'View Content'
      default:
        return 'Content'
    }
  }

  console.log({ formData })

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' },
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography variant="h6">{getDialogTitle()}</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <Typography>Loading...</Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomInput
                  label="Name"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={isViewMode}
                  required
                  id="name"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomSelect
                  label="Type"
                  value={formData.type || CONTENT_TYPES.SINGLE_VIDEO}
                  onChangeEvent={(e) => handleInputChange('type', e.target.value)}
                  disabled={isViewMode}
                  id="type"
                  options={[
                    {
                      id: CONTENT_TYPES.SINGLE_VIDEO,
                      title: 'Single Video',
                      name: 'Single Video',
                    },
                    {
                      id: CONTENT_TYPES.PLAYLIST,
                      title: 'Playlist',
                      name: 'Playlist',
                    },
                  ]}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  selectType={selectTypes.string}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomInput
                  label="Content ID"
                  value={formData.contentId || ''}
                  onChange={(e) => handleInputChange('contentId', e.target.value)}
                  disabled={isViewMode}
                  required
                  id="contentId"
                  subLabel="YouTube video ID or playlist ID"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomInput
                  label="Content Number"
                  value={formData.contentNumber || ''}
                  onChange={(e) => handleInputChange('contentNumber', e.target.value)}
                  disabled={isViewMode}
                  id="contentNumber"
                  subLabel="Optional ordering number"
                />
              </Grid>

              {/* Video Player Preview */}
              {formData.contentId && formData.type === CONTENT_TYPES.SINGLE_VIDEO && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Video Preview
                  </Typography>
                  <Box sx={{ width: '100%', maxWidth: 560, height: 315, mb: 2 }}>
                    <ReactPlayer
                      url={`https://www.youtube.com/watch?v=${formData.contentId}`}
                      width="100%"
                      height="100%"
                      controls
                    />
                  </Box>
                </Grid>
              )}

              {/* Playlist Video IDs */}
              {formData.type === CONTENT_TYPES.PLAYLIST && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Playlist Videos
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Add individual videos that are part of this playlist
                    </Typography>
                  </Grid>

                  {!isViewMode && (
                    <Grid item xs={12}>
                      <Card sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {editingPlaylistItem !== null ? 'Edit Video' : 'Add New Video'}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <CustomInput
                              label="Video Name"
                              value={playlistItemForm.name}
                              onChange={(e) =>
                                setPlaylistItemForm((prev) => ({ ...prev, name: e.target.value }))
                              }
                              id="videoName"
                              required
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <CustomInput
                              label="YouTube Video ID"
                              value={playlistItemForm.contentId}
                              onChange={(e) =>
                                setPlaylistItemForm((prev) => ({
                                  ...prev,
                                  contentId: e.target.value,
                                }))
                              }
                              id="videoContentId"
                              required
                              subLabel="e.g., GFt938Hn1PE"
                            />
                          </Grid>
                          {playlistItemForm.contentId && (
                            <Grid item xs={12}>
                              <Box sx={{ width: '100%', maxWidth: 400, height: 225 }}>
                                <ReactPlayer
                                  url={`https://www.youtube.com/watch?v=${playlistItemForm.contentId}`}
                                  width="100%"
                                  height="100%"
                                  controls
                                />
                              </Box>
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                onClick={handleAddPlaylistItem}
                                startIcon={<Add />}
                                disabled={
                                  !playlistItemForm.name.trim() ||
                                  !playlistItemForm.contentId.trim()
                                }
                              >
                                {editingPlaylistItem !== null ? 'Update Video' : 'Add Video'}
                              </Button>
                              {editingPlaylistItem !== null && (
                                <Button
                                  variant="outlined"
                                  onClick={() => {
                                    setEditingPlaylistItem(null)
                                    setPlaylistItemForm({ name: '', contentId: '' })
                                  }}
                                >
                                  Cancel
                                </Button>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {formData.playlistVideoIds?.map((video, index) => (
                        <Card key={index} sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {video.name || `Video ${index + 1}`}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Content ID: {video.contentId}
                              </Typography>
                            </Box>
                            <Box sx={{ width: 160, height: 90 }}>
                              <ReactPlayer
                                url={`https://www.youtube.com/watch?v=${video.contentId}`}
                                width="100%"
                                height="100%"
                                light
                              />
                            </Box>
                            {!isViewMode && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditPlaylistItem(index)}
                                  color="primary"
                                >
                                  <Edit />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveVideoId(index)}
                                  color="error"
                                >
                                  <Delete />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        </Card>
                      ))}
                    </Box>
                  </Grid>
                </>
              )}

              {/* Timing Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Timing Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ '& .MuiTextField-root': { width: '100%' } }}>
                  <DateTimePicker
                    label="Published Time"
                    value={formData.publishedTime}
                    onChange={(value) => handleInputChange('publishedTime', value)}
                    disabled={isViewMode}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ '& .MuiTextField-root': { width: '100%' } }}>
                  <DateTimePicker
                    label="Premier Time"
                    value={formData.premierTime}
                    onChange={(value) => handleInputChange('premierTime', value)}
                    disabled={isViewMode}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ '& .MuiTextField-root': { width: '100%' } }}>
                  <DateTimePicker
                    label="Available Till"
                    value={formData.availableTill}
                    onChange={(value) => handleInputChange('availableTill', value)}
                    disabled={isViewMode}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                </Box>
              </Grid>

              {/* User Access Control */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  User Access Control
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomSelect
                  label="Access Type"
                  value={formData.exclInclType}
                  onChangeEvent={(e) => handleInputChange('exclInclType', e.target.value)}
                  disabled={isViewMode}
                  id="exclInclType"
                  options={[
                    {
                      id: EXCL_INCL_TYPES.INCLUDE,
                      title: 'Include Users',
                      name: 'Include Users',
                    },
                    {
                      id: EXCL_INCL_TYPES.EXCLUDE,
                      title: 'Exclude Users',
                      name: 'Exclude Users',
                    },
                  ]}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  selectType={selectTypes.string}
                  subLabel="Include: Only specified users can access. Exclude: All except specified users can access."
                />
              </Grid>

              {!isViewMode && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                    <CustomInput
                      label="User ID"
                      value={newUserId}
                      onChange={(e) => setNewUserId(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddUserId()}
                      id="newUserId"
                      subLabel="Enter user ID (USR-xxx...) and click on Add to add it to the list."
                    />
                    <Button
                      variant="outlined"
                      onClick={handleAddUserId}
                      size="large"
                      startIcon={<Add />}
                      disabled={!newUserId.trim()}
                      sx={{ mt: 1 }}
                    >
                      Add
                    </Button>
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.exclInclUserIds?.map((userId, index) => (
                    <Chip
                      key={index}
                      label={userId}
                      onDelete={!isViewMode ? () => handleRemoveUserId(index) : undefined}
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>{isViewMode ? 'Close' : 'Cancel'}</Button>
          {!isViewMode && (
            <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  )
}
